// "Driver Assignment" change panel — assigns a driver to a vehicle as either
// the Principal or Occasional operator, with a percentage of use for that
// unit (percentages on a unit should total 100%).
//
// Rendered in the v1-style RightPanel (blue header + Close button) per the
// mockup, with an Add/Change checkbox toggle at the top:
//   - Add mode    -> create a brand new assignment record.
//   - Change mode -> pick an existing assignment (by driver) to replace,
//                    then enter the new assignment's details, mirroring the
//                    vehicle/driver/interested-party substitution pattern.
//
// Both the Vehicle and Driver dropdowns are restricted to ACTIVE records
// only (filtered client-side, matching AddressChangeInformationV2's approach
// so the shared hooks' behaviour for other consumers stays unchanged).

import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import produce from "immer";
import { RightPanel } from "../../../../../../../../components/RightPanel/RightPanel";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../../components/formsComponents";
import { useAutomobilePolicyVehcileInfo } from "../../../hooks/useAutomobileVehicleInfo";
import { useAutomobilePolicyDriverInfo } from "../../../hooks/useAutomobilePolicyDriverInfo";
import { IAutomobilePolicyState } from "../../../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReq.types";

interface IDriverAssignmentChangeV2Props {
  open: boolean;
  // "Add" | "Change" chosen on the board.
  mode?: string | null;
  values: IAutomobilePolicyState;
  setValues: (values: any, shouldValidate?: boolean) => any;
  customerPolicyId?: number;
  autoSaveData?: (values: IAutomobilePolicyState) => void;
  onClose: () => void;
  isStatusCompleted?: boolean;
  // When set, edits the existing record at this index (from "Edit" in
  // Changes Requested) instead of creating a new one.
  editIndex?: number;
}

const ASSIGNMENT_TYPE_OPTIONS: ("Principal" | "Occasional")[] = [
  "Principal",
  "Occasional",
];

export const DriverAssignmentChangeV2: React.FC<
  IDriverAssignmentChangeV2Props
> = ({
  open,
  mode,
  values,
  setValues,
  customerPolicyId,
  autoSaveData = () => {},
  onClose,
  isStatusCompleted,
  editIndex,
}) => {
  const { vehiclesList } = useAutomobilePolicyVehcileInfo(customerPolicyId);
  const { driverList } = useAutomobilePolicyDriverInfo(customerPolicyId);

  // Active-only, filtered client-side (the shared hooks don't filter by
  // status themselves — matching AddressChangeInformationV2's approach so
  // other consumers of these hooks are unaffected).
  const activeVehicleDropdown = React.useMemo(
    () =>
      vehiclesList
        .filter((vehicle) => vehicle.status === "ACTIVE")
        .map((vehicle) => ({
          label: `${vehicle.incremental_number ?? "--"} (VIN: ${
            vehicle.vin || "N/A"
          }, Year: ${vehicle.year}, Make: ${vehicle.make}, Model: ${
            vehicle.model
          })`,
          value: vehicle.incremental_number ?? "",
        })),
    [vehiclesList]
  );

  const activeDriverDropdown = React.useMemo(
    () =>
      driverList
        .filter((driver) => driver.status === "ACTIVE")
        .map((driver) => ({
          label: `${driver.driver_number || "--"} (Driver Name: ${
            driver.driver_name || "N/A"
          })`,
          value: driver.driver_number || "",
        })),
    [driverList]
  );

  const assignmentChange = values.driver_assignment_change;
  const isEdit = editIndex !== undefined;
  const existing = isEdit
    ? assignmentChange?.records?.[editIndex as number]
    : undefined;
  const effectiveMode = mode || assignmentChange?.type || "Add";
  const isChangeMode = effectiveMode === "Change";

  // Local draft for the single record being added/substituted/edited.
  // Committed to values.driver_assignment_change.records on "Add to request".
  // mode/delete_driver_assignment are carried through unchanged when editing
  // (the Add/Change picker is hidden in edit mode) and re-derived from the
  // panel's current mode selection when creating a new record.
  const [draft, setDraft] = React.useState<{
    vehicle_number: number | null;
    driver_number: number | null;
    assignment_type: "Principal" | "Occasional" | null;
    percentage: number | null;
    notes: string | null;
    mode?: "Add" | "Change" | null;
    delete_driver_assignment?: string | null;
  }>(
    existing
      ? { ...existing }
      : {
          vehicle_number: null,
          driver_number: null,
          assignment_type: null,
          percentage: null,
          notes: null,
          mode: null,
          delete_driver_assignment: null,
        }
  );

  // Which existing assignment (by driver_number) is being replaced, only
  // relevant in Change mode.
  const [substitutionTarget, setSubstitutionTarget] = React.useState<
    string | number
  >(assignmentChange?.delete_driver_assignment || "");

  const handleModeChange = (nextMode: "Add" | "Change") => {
    setValues(
      produce(values, (draftValues) => {
        if (!draftValues.driver_assignment_change) {
          draftValues.driver_assignment_change = {
            type: null,
            delete_driver_assignment: null,
            records: [],
          };
        }
        draftValues.driver_assignment_change.type = nextMode;
      })
    );
  };

  const handleSave = () => {
    const newValues = produce(values, (draftValues) => {
      if (!draftValues.driver_assignment_change) {
        draftValues.driver_assignment_change = {
          type: null,
          delete_driver_assignment: null,
          records: [],
        };
      }
      // The mode is stamped onto the record itself (not the shared section
      // type) so it stays fixed for that record even if a later Add/Change
      // click on the board flips the section-level mode for the NEXT record.
      const recordToSave = {
        ...draft,
        mode: isEdit ? draft.mode : isChangeMode ? "Change" : "Add",
        delete_driver_assignment: isEdit
          ? draft.delete_driver_assignment
          : isChangeMode
          ? String(substitutionTarget)
          : null,
      };
      if (isEdit) {
        draftValues.driver_assignment_change.records[
          editIndex as number
        ] = recordToSave;
      } else {
        draftValues.driver_assignment_change.records.push(recordToSave);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    onClose();
  };

  const canSave =
    !!draft.vehicle_number &&
    !!draft.driver_number &&
    !!draft.assignment_type &&
    (!isChangeMode || !!substitutionTarget);

  return (
    <RightPanel
      open={open}
      heading="Driver Assignment Change"
      onClose={onClose}
      actionButtons={() => (
        <Stack direction="row" justifyContent="flex-start" spacing={2}>
          <Button
            variant="contained"
            disabled={!canSave || isStatusCompleted}
            onClick={handleSave}
          >
            {isEdit ? "Save" : "Add to request"}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      )}
    >
      {!isEdit && (
        <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center">
            <Checkbox
              checked={!isChangeMode}
              onChange={(e) => {
                if (e.target.checked) {
                  handleModeChange("Add");
                }
              }}
            />
            <Typography>Add</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Checkbox
              checked={isChangeMode}
              onChange={(e) => {
                if (e.target.checked) {
                  handleModeChange("Change");
                }
              }}
            />
            <Typography>Change</Typography>
          </Stack>
        </Stack>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Assigns an operator to a unit. Use percentages on a unit should total
        100%.
      </Typography>

      {isChangeMode && !isEdit && (
        <Box sx={{ mb: 2 }}>
          <CustomFormLabel>Assignment to Replace</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="substitution_target"
            value={substitutionTarget}
            onChange={(e: any) => setSubstitutionTarget(e.target.value)}
            placeholder="Select One"
            options={activeDriverDropdown}
          />
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <CustomFormLabel>Vehicle</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="vehicle_number"
            value={draft.vehicle_number ?? ""}
            onChange={(e: any) =>
              setDraft((prev) => ({
                ...prev,
                vehicle_number: e.target.value,
              }))
            }
            placeholder="Select One"
            options={activeVehicleDropdown}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomFormLabel>Driver</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="driver_number"
            value={draft.driver_number ?? ""}
            onChange={(e: any) =>
              setDraft((prev) => ({
                ...prev,
                driver_number: e.target.value,
              }))
            }
            placeholder="Select One"
            options={activeDriverDropdown}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <CustomFormLabel>Assignment Type</CustomFormLabel>
          <ToggleButtonGroup
            value={draft.assignment_type}
            exclusive
            size="small"
            onChange={(_e, value) => {
              if (value) {
                setDraft((prev) => ({ ...prev, assignment_type: value }));
              }
            }}
          >
            {ASSIGNMENT_TYPE_OPTIONS.map((option) => (
              <ToggleButton key={option} value={option}>
                {option}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomFormLabel>Percentage</CustomFormLabel>
          <CustomTextField
            fullWidth
            type="number"
            placeholder="70"
            value={draft.percentage ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDraft((prev) => ({
                ...prev,
                percentage: e.target.value ? Number(e.target.value) : null,
              }))
            }
          />
        </Grid>
      </Grid>

      <Box>
        <CustomFormLabel>Notes</CustomFormLabel>
        <CustomTextField
          fullWidth
          multiline
          rows={4}
          value={draft.notes || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraft((prev) => ({ ...prev, notes: e.target.value }))
          }
        />
      </Box>
    </RightPanel>
  );
};
